import { z } from 'zod';
import { idArray } from './shared.schema';

export const invoiceMonthlyDetailFeeTypeSchema = z.enum([
	'Monthly',
	'Manual Monthly',
	'Activation',
	'Access Device',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Transaction',
	'Mail Invoice',
	'Vehicle Surcharge',
	'Lost Device',
	'Beginning Balance',
	'Validation',
	'Hotel Storage',
	'Office/Retail Rent',
	'Miscellaneous',
]);

export const invoiceMonthlyDetailFeeTypes = invoiceMonthlyDetailFeeTypeSchema.options;

export type InvoiceMonthlyDetailFeeType = z.infer<typeof invoiceMonthlyDetailFeeTypeSchema>;

export const invoiceMonthlyDetailDefaultFeeTypes = [
	'Monthly',
	'Manual Monthly',
	'Activation',
	'Access Device',
	'Credit Card',
	'Late',
	'Recurring Late',
	'Transaction',
	'Mail Invoice',
	'Vehicle Surcharge',
] as const satisfies readonly InvoiceMonthlyDetailFeeType[];

const filterSchema = z.discriminatedUnion('type', [
	z.object({
		type: z.literal('accountId'),
		value: z.number().int(),
	}),
	z.object({
		type: z.literal('subscriptionName'),
		value: z.string(),
	}),
]);

export const subscriberTypeSchema = z.enum(['All', 'Business', 'Individual']);

export type SubscriberType = z.infer<typeof subscriberTypeSchema>;

export const invoiceMonthlyDetailParametersSchema = z
	.object({
		accountFilter: filterSchema.optional(),
		subscriberType: subscriberTypeSchema.optional(),
		feeTypes: z.array(invoiceMonthlyDetailFeeTypeSchema).min(1),
		invoiceStartDate: z.iso.date().optional(),
		invoiceEndDate: z.iso.date().optional(),
		parkingPeriodStartDate: z.iso.date().optional(),
		parkingPeriodEndDate: z.iso.date().optional(),
		areaIds: idArray.optional(),
		branchIds: idArray.optional(),
		districtIds: idArray.optional(),
		facilityIds: z.array(z.string()).optional(),
	})
	.superRefine((value, ctx) => {
		const hasInvoiceStartDate = value.invoiceStartDate !== undefined;
		const hasParkingPeriodStart = value.parkingPeriodStartDate !== undefined;

		if (!hasInvoiceStartDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceStartDate'],
				message: 'Either invoiceStartDate or parkingPeriodStartDate is required',
			});
		}

		if (value.invoiceEndDate && !hasInvoiceStartDate) {
			ctx.addIssue({
				code: 'custom',
				path: ['invoiceEndDate'],
				message: 'Cannot specify invoiceEndDate without invoiceStartDate',
			});
		}

		if (value.parkingPeriodEndDate && !hasParkingPeriodStart) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Cannot specify parkingPeriodEndDate without parkingPeriodStartDate',
			});
		}

		if (
			value.parkingPeriodStartDate &&
			value.parkingPeriodEndDate &&
			value.parkingPeriodEndDate < value.parkingPeriodStartDate
		) {
			ctx.addIssue({
				code: 'custom',
				path: ['parkingPeriodEndDate'],
				message: 'Parking period end must be on or after parking period start',
			});
		}
	});

export type InvoiceMonthlyDetailParameters = z.infer<typeof invoiceMonthlyDetailParametersSchema>;
